export interface ParsedSearchInput {
    query: string;
    tags: string[];
    activeTagQuery: string | null;
}

const acronymTagParts = new Set(['bjj', 'etk', 'k1', 'k2', 'mma', 'ufc']);

const formatTagPart = (part: string) => {
    if (/^\d+$/.test(part)) {
        return part;
    }

    const lowerPart = part.toLowerCase();
    if (acronymTagParts.has(lowerPart) || /^[A-Z0-9]{2,3}$/.test(part)) {
        return part.toUpperCase();
    }

    if (/[a-z]/.test(part) && /[A-Z]/.test(part)) {
        return part.charAt(0).toUpperCase() + part.slice(1);
    }

    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
};

const normalizeTagValue = (value: string) => {
    const trimmed = value.trim().replace(/^#+/, '').replace(/[.,;:]+$/, '').trim();
    if (trimmed.length === 0) {
        return null;
    }

    const parts = trimmed.match(/[\p{L}\p{N}]+/gu) ?? [];
    const normalized = parts.map(formatTagPart).join('');
    return normalized.length > 0 ? normalized : null;
};

const normalizeTagKey = (value: string) => normalizeTagValue(value)?.toLowerCase() ?? null;

const getActiveTagQuery = (input: string) => {
    const hashIndex = input.lastIndexOf('#');
    if (hashIndex < 0) {
        return null;
    }

    const tail = input.slice(hashIndex + 1);
    if (tail.length === 0) {
        return '';
    }

    return tail.includes(' ') ? null : tail.trim();
};

export const parseSearchInput = (input: string): ParsedSearchInput => {
    const tokens = input.trim().length > 0 ? input.trim().split(/\s+/) : [];
    const tags: string[] = [];
    const keywords: string[] = [];
    const seenTags = new Set<string>();

    tokens.forEach((token) => {
        if (token.startsWith('#') && token.length > 1) {
            const normalized = normalizeTagValue(token);
            const key = normalizeTagKey(token);
            if (normalized && key && !seenTags.has(key)) {
                tags.push(normalized);
                seenTags.add(key);
            }
        } else if (token.length > 0) {
            keywords.push(token);
        }
    });

    return {
        query: keywords.join(' ').trim(),
        tags,
        activeTagQuery: getActiveTagQuery(input)
    };
};

export const buildSearchInput = (query: string, tags: string[]) => {
    const cleanedQuery = query.trim();
    const normalizedTags = tags
        .map((tag) => normalizeTagValue(tag))
        .filter((tag): tag is string => Boolean(tag));
    const tagTokens = normalizedTags.map((tag) => `#${tag}`);

    return [cleanedQuery, ...tagTokens].filter((value) => value.length > 0).join(' ').trim();
};

export const normalizeSearchParamTags = (values: string[]) => {
    const normalizedTags: string[] = [];
    const seen = new Set<string>();

    values.forEach((value) => {
        if (!value) {
            return;
        }

        value.split(',').forEach((token) => {
            const normalized = normalizeTagValue(token);
            if (!normalized) {
                return;
            }

            const key = normalized.toLowerCase();
            if (seen.has(key)) {
                return;
            }

            seen.add(key);
            normalizedTags.push(normalized);
        });
    });

    return normalizedTags;
};

export const toggleTagInInput = (input: string, tagName: string) => {
    const targetKey = normalizeTagKey(tagName);
    if (!targetKey) {
        return input;
    }

    const tokens = input.trim().length > 0 ? input.trim().split(/\s+/) : [];
    const filteredTokens = tokens.filter((token) => {
        if (!token.startsWith('#')) {
            return true;
        }

        const tokenKey = normalizeTagKey(token);
        return tokenKey !== targetKey;
    });

    const hadTag = filteredTokens.length !== tokens.length;
    if (!hadTag) {
        const normalized = normalizeTagValue(tagName);
        if (normalized) {
            filteredTokens.push(`#${normalized}`);
        }
    }

    return filteredTokens.join(' ').trim();
};

export const stripTagsFromInput = (input: string) => {
    const tokens = input.trim().length > 0 ? input.trim().split(/\s+/) : [];
    const keywords = tokens.filter((token) => !(token.startsWith('#') && normalizeTagValue(token)));
    return keywords.join(' ').trim();
};

export const normalizeTagNameKey = (value: string) => normalizeTagKey(value) ?? '';
